import { useCallback, useMemo, useState } from 'react'
import { DateTime } from 'luxon'
import { buildWeeks, CalendarWeek, maxLastMonday } from './timeCalendarHelper'

export type CalendarWindowProps = {
    // From old to new: the last row is the newest week (next week by default) with the current week above it.
    weeks: CalendarWeek[]
    // The month the header shows; derived from the newest visible week and therefore follows the scrolling.
    visibleMonth: DateTime
    // ISO date of the first day that has to be fetched (monday of the oldest week, or earlier when the visible
    // month starts before it, so the month tooltip can show a complete month).
    from: string
    // ISO date of the last day that has to be fetched.
    to: string
    canScrollDown: boolean
    // Earlier weeks; the window walks into the past.
    scrollUp: () => void
    // Later weeks, up to next week.
    scrollDown: () => void
    jumpMonth: (delta: number) => void
}

/**
 * Keeps the visible window of weeks. It deliberately does not touch the overview date filter: the calendar always
 * shows whole weeks around today, while the list keeps its own date filter until the user clicks a day.
 */
export default function useCalendarWindow(): CalendarWindowProps {
    const [lastMonday, setLastMonday] = useState<DateTime>(() => maxLastMonday())

    const weeks = useMemo(() => buildWeeks(lastMonday), [lastMonday])
    const visibleMonth = useMemo(() => lastMonday.startOf('month'), [lastMonday])

    const scrollUp = useCallback(() => setLastMonday(current => current.minus({ weeks: 1 })), [])
    const scrollDown = useCallback(() => setLastMonday(current => DateTime.min(current.plus({ weeks: 1 }), maxLastMonday())), [])

    // End the table on the requested month, so the weeks above it walk back through that month.
    const jumpMonth = useCallback(
        (delta: number) =>
            setLastMonday(current => DateTime.min(current.startOf('month').plus({ months: delta }).endOf('month').startOf('week'), maxLastMonday())),
        [],
    )

    const oldestMonday = weeks[0].monday
    const newestSunday = weeks[weeks.length - 1].monday.plus({ days: 6 })

    return {
        weeks,
        visibleMonth,
        from: DateTime.min(oldestMonday, visibleMonth).toISODate(),
        to: DateTime.max(newestSunday, visibleMonth.endOf('month')).toISODate(),
        canScrollDown: lastMonday < maxLastMonday(),
        scrollUp,
        scrollDown,
        jumpMonth,
    }
}
